import createSelector from 'web-app/util/custom-create-selector';
import {getLoginId} from 'web-app/react/session/account/selectors';
import {inboxType} from 'web-app/react/inbox/conversation-center/selectors';
import {InboxTypes} from 'web-app/react/inbox/constants';

import {areSelectedInDifferentContext} from './helpers';

export const receivers = state => state.inbox.newInboxConversation.receivers;
export const activeNodePath = state => state.inbox.newInboxConversation.activeNodePath;
export const selectedReceivers = state => state.inbox.newInboxConversation.selectedReceivers;

const DAYCARE_FAMLY_ID_PREFIX = 'Famly.Daycare:Group|';

export const activeNodePathKey = createSelector(activeNodePath, path =>
    path.reduce((acc, nodeIndex) => (acc ? `${acc}.${nodeIndex}` : String(nodeIndex)), ''),
);

const getReceiverFamlyId = (senderId, participants, inboxType) => {
    if (!participants) {
        return '';
    }

    let counterpart;

    if (inboxType !== InboxTypes.OWN) {
        counterpart = participants.find(p => !p.includes(senderId) && !p.includes(DAYCARE_FAMLY_ID_PREFIX));
    } else {
        counterpart = participants.find(p => !p.includes(senderId));
    }

    return counterpart;
};

export const activeReceivers = createSelector(receivers, activeNodePath, (allReceivers, activePath) => {
    if (!activePath.size) {
        return {nodes: allReceivers};
    }

    const rootNode = allReceivers.get(activePath.first());
    const activeReceivers = activePath.shift().reduce((acc, nodeIndex) => {
        if (acc.nodes) {
            return acc.nodes[nodeIndex];
        }
        return acc;
    }, rootNode);

    return activeReceivers;
});

export const showingDetails = createSelector(activeNodePath, activePath => Boolean(activePath.size));

export const makeSelectedCount = () =>
    createSelector(
        selectedReceivers,
        activeNodePathKey,
        (state, props) => props.itemIndex,
        (receivers, pathKey, itemIndex) => {
            return receivers.keySeq().reduce((acc, key) => {
                const itemPath = pathKey ? `${pathKey}.${itemIndex}` : itemIndex;
                if (String(key) === itemPath) {
                    return acc + receivers.get(key).size;
                }
                return acc;
            }, 0);
        },
    );

const getReceivers = createSelector(selectedReceivers, receiversMap => receiversMap.toList().flatMap(_ => _));

export const receiversWithParticipants = createSelector(getReceivers, receivers =>
    receivers
        .filter(receiver => receiver.participants && receiver.participants.length)
        .groupBy(receiver => receiver.participants[1])
        .map(r => r.first())
        .toList(),
);

export const receiversWithNoParticipants = createSelector(getReceivers, receivers =>
    receivers.filter(receiver => !receiver.participants || !receiver.participants.length),
);

export const receiverIds = createSelector(getLoginId, getReceivers, inboxType, (senderId, receivers, inboxType) => {
    const receiverIds = receivers.flatMap(receiver => receiver.participants);
    const senderFamlyId = `Famly.Login:Login|${senderId}`;

    if (inboxType === InboxTypes.STAR) {
        return receiverIds.filter(id => id !== senderFamlyId);
    }

    return receiverIds.filter(id => id !== senderFamlyId).push(senderFamlyId);
});

export const makeIsReceiverSelected = () =>
    createSelector(
        getLoginId,
        receiverIds,
        inboxType,
        (state, props) => props.receiver,
        (senderLoginId, selectedReceiverIds, type, {participants}) => {
            return participants && selectedReceiverIds.includes(getReceiverFamlyId(senderLoginId, participants, type));
        },
    );

export const makeIsSelectedInDifferentContext = () =>
    createSelector(
        receiverIds,
        (state, props) => props.receiver,
        (selectedReceiverIds, {participants}) => {
            if (!participants || !selectedReceiverIds) {
                return false;
            }

            return areSelectedInDifferentContext(participants, selectedReceiverIds);
        },
    );

export const makeReceiverId = () =>
    createSelector(
        getLoginId,
        (state, props) => props.receiver && props.receiver.participants,
        (senderId, participants) => {
            const receiverFamlyId = getReceiverFamlyId(senderId, participants);
            if (!receiverFamlyId) {
                return null;
            }

            return receiverFamlyId.split('|')[1];
        },
    );
